import React, { useState, useEffect } from "react";
import { Form, Input, Button, Spin, Row, Col, DatePicker, Select, InputNumber } from "antd";
import { ReactSVG } from "react-svg";

import { DATE_FORMAT_FRONTEND, STATUS } from "../../../constants";
import { getTranslation } from "../../../utils/helper";
import TagInput from "../../Elements/TagInput";
import { useSelector } from "react-redux";
import LabelComponent from "../../Elements/LabelComponent";

const OrderInfoForm = ({ form, isFetching, groups, handleSubmit, handleCancel }) => {
  const user = useSelector(state => state.auth.user);
  const setting = user.company;
  const [types, setTypes] = useState([]);
  const [labelIds, setLabelIds] = useState([]);

  useEffect(() => {
    setTypes(form.getFieldValue("types"));
    setLabelIds(form.getFieldValue("labels"));
  }, [form]);

  const handleOnFinish = values => {
    const params = { ...values, requiredSkills: types };
    params.labels = labelIds;
    params.fields = {};
    values.fields.forEach(field => {
      params.fields[field.name] = field.value;
    });
    handleSubmit(params);
  };

  return (
    <Spin spinning={isFetching}>
      <div className="OrderInfoForm">
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleOnFinish}
          onKeyPress={e => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}>
          <div className="FormHeader">
            <div className="left">{getTranslation(setting, "Orders", "Edit Order Info")}</div>
          </div>
          <div className="FormContent">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={getTranslation(setting, "Orders", "Order Ref")} name="orderRef">
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Order Ref")} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={getTranslation(setting, "Orders", "Date")}
                  name="deliveryDate"
                  rules={[{ required: true, message: "Required" }]}>
                  <DatePicker
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Date")}
                    format={DATE_FORMAT_FRONTEND}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={getTranslation(setting, "Orders", "Status")} name="status">
                  <Select size="large" showSearch optionFilterProp="children">
                    {STATUS.map(c => (
                      <Select.Option key={c.code} value={c.code}>
                        {getTranslation(setting, "Orders", c.code)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={getTranslation(setting, "Orders", "User Group")} name="group">
                  <Select size="large" showSearch optionFilterProp="children">
                    {groups.map(c => (
                      <Select.Option key={c._id} value={c._id}>
                        {c.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={getTranslation(setting, "Orders", "Load")} name="load">
                  <InputNumber
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Load")}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label={getTranslation(setting, "Orders", "Labels")}>
              <LabelComponent labelIds={labelIds} setLabelIds={setLabelIds} />
            </Form.Item>
            <Form.Item label={getTranslation(setting, "Orders", "Types")}>
              <TagInput tags={types} onChange={values => setTypes(values)} />
            </Form.Item>
            {form.getFieldValue("routePlanType") === "PDP" && (
              <Form.Item label={getTranslation(setting, "Orders", "Pickup Notes")} name="pickupNotes">
                <Input.TextArea rows={4} placeholder={getTranslation(setting, "Orders", "Pickup Notes")} />
              </Form.Item>
            )}
            <Form.Item label={getTranslation(setting, "Orders", "Delivery Notes")} name="deliveryNotes">
              <Input.TextArea rows={4} placeholder={getTranslation(setting, "Orders", "Delivery Notes")} />
            </Form.Item>
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">{getTranslation(setting, "Orders", "Custom Fields")}</div>
              </div>
              <div className="SectionContent">
                <Form.List name="fields" label={getTranslation(setting, "Orders", "Custom Fields")}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(field => (
                        <div key={field.key} className="Field">
                          <div className="FieldName">
                            <Form.Item
                              {...field}
                              name={[field.name, "name"]}
                              rules={[{ required: true, message: "Required" }]}>
                              <Input size="large" placeholder={getTranslation(setting, "Orders", "Field Title")} />
                            </Form.Item>
                          </div>
                          <div className="FieldValue">
                            <Form.Item
                              {...field}
                              name={[field.name, "value"]}
                              rules={[{ required: true, message: "Required" }]}>
                              <Input size="large" placeholder={getTranslation(setting, "Orders", "Field Input")} />
                            </Form.Item>
                          </div>
                          <div className="FieldAction">
                            <Button key="delete" onClick={() => remove(field.name)} size="large">
                              <ReactSVG className="icon" src="/icons/delete.svg" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <Form.Item>
                        <Button type="primary" onClick={() => add()} className="AddCustomFieldBtn">
                          {getTranslation(setting, "Orders", "Add Custom Field")}
                          <ReactSVG className="icon" src="/icons/add.svg" />
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </div>
            </div>
          </div>
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <Button key="cancel" onClick={handleCancel}>
                {getTranslation(setting, "Common", "Cancel")}
              </Button>
              <Button htmlType="submit" type="primary">
                {getTranslation(setting, "Common", "Update")}
                <ReactSVG className="icon" src="/icons/submit.svg" />
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default OrderInfoForm;
